import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import api from "../../../api/axios";

import { tokenStorage } from "../../../utils/tokenStorage";

/**
 * LOGIN
 */
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/v1/power/auth/login", {
        email,
        password,
      });

      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error?.status,
        message: error?.message || "Login mislukt.",
      });
    }
  },
);

/**
 * INITIALIZE MFA SETUP
 */
export const initializeLoginMfa = createAsyncThunk(
  "auth/initializeLoginMfa",
  async ({ mfaToken }, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/v1/power/auth/setup-mfa", {
        mfaToken,
      });

      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error?.status,
        message: error?.message || "MFA setup kon niet gestart worden.",
      });
    }
  },
);

/**
 * VERIFY MFA
 */
export const verifyLoginMfa = createAsyncThunk(
  "auth/verifyLoginMfa",
  async ({ mfaToken, code }, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/v1/power/auth/verify-mfa", {
        mfaToken,
        code,
      });

      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error?.status,
        message: error?.message || "MFA-code is ongeldig.",
      });
    }
  },
);

/**
 * REFRESH SESSION
 */
export const refreshAuthSession = createAsyncThunk(
  "auth/refreshAuthSession",
  async (_, { rejectWithValue }) => {
    try {
      const refreshToken = tokenStorage.getRefreshToken();

      if (!refreshToken) {
        throw new Error("Refresh token ontbreekt.");
      }

      const response = await api.post("/api/v1/power/auth/refresh", {
        refreshToken,
      });

      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error?.status,
        message: error?.message || "Sessie vernieuwen mislukt.",
      });
    }
  },
);

/**
 * CURRENT USER
 */
export const getCurrentUser = createAsyncThunk(
  "auth/getCurrentUser",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/api/v1/power/auth/me");

      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error?.status,
        message: error?.message || "Gebruiker laden mislukt.",
      });
    }
  },
);

/**
 * LOGOUT
 */
export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue }) => {
    try {
      const refreshToken = tokenStorage.getRefreshToken();

      if (refreshToken) {
        await api.post("/api/v1/power/auth/logout", {
          refreshToken,
        });
      }

      return true;
    } catch (error) {
      return rejectWithValue({
        status: error?.status,
        message: error?.message || "Logout mislukt.",
      });
    }
  },
);

const storedUser = tokenStorage.getUser();

const storedAccessToken = tokenStorage.getAccessToken();

/**
 * INITIAL STATE
 */
const initialState = {
  status: storedAccessToken && storedUser ? "authenticated" : "idle",

  user: storedUser,

  accessToken: storedAccessToken,

  mfaToken: null,

  mfaSetupData: null,

  isLoading: false,

  isBootstrapping: false,

  error: null,
};

/**
 * NORMALIZE AUTH PAYLOAD
 */
function normalizeAuthPayload(payload) {
  const data = payload?.data || {};

  return {
    accessToken: data.access_token || data.accessToken || null,

    refreshToken: data.refresh_token || data.refreshToken || null,

    user: data.user || null,
  };
}

/**
 * SLICE
 */
const authSlice = createSlice({
  name: "auth",

  initialState,

  reducers: {
    clearAuthError: (state) => {
      state.error = null;
    },

    clearMfaFlow: (state) => {
      state.mfaToken = null;

      state.mfaSetupData = null;

      state.error = null;

      state.status = "idle";
    },

    logout: (state) => {
      tokenStorage.clearAuth();

      state.status = "idle";

      state.user = null;

      state.accessToken = null;

      state.mfaToken = null;

      state.mfaSetupData = null;

      state.isLoading = false;

      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder

      /**
       * LOGIN
       */
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;

        state.error = null;

        state.mfaToken = null;

        state.mfaSetupData = null;
      })

      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;

        const data = action.payload?.data || {};

        const status = data.status;

        /**
         * MFA SETUP REQUIRED
         */
        if (status === "MFA_SETUP_REQUIRED") {
          state.status = "mfa_setup_required";

          state.mfaToken = data.mfa_token || null;

          state.user = data.user || null;

          return;
        }

        /**
         * MFA REQUIRED
         */
        if (status === "MFA_REQUIRED") {
          state.status = "mfa_required";

          state.mfaToken = data.mfa_token || null;

          state.user = data.user || null;

          return;
        }

        state.status = "error";

        state.error = "Onverwachte login response.";
      })

      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;

        state.status = "error";

        state.error = action.payload?.message || "Login mislukt.";
      })

      /**
       * INITIALIZE MFA
       */
      .addCase(initializeLoginMfa.pending, (state) => {
        state.isLoading = true;

        state.error = null;
      })

      .addCase(initializeLoginMfa.fulfilled, (state, action) => {
        state.isLoading = false;

        state.mfaSetupData = action.payload?.data || null;
      })

      .addCase(initializeLoginMfa.rejected, (state, action) => {
        state.isLoading = false;

        state.error = action.payload?.message || "MFA setup mislukt.";
      })

      /**
       * VERIFY MFA
       */
      .addCase(verifyLoginMfa.pending, (state) => {
        state.isLoading = true;

        state.error = null;
      })

      .addCase(verifyLoginMfa.fulfilled, (state, action) => {
        state.isLoading = false;

        const auth = normalizeAuthPayload(action.payload);

        if (auth.accessToken && auth.refreshToken && auth.user) {
          tokenStorage.saveAuth(auth);

          state.status = "authenticated";

          state.user = auth.user;

          state.accessToken = auth.accessToken;

          state.mfaToken = null;

          state.mfaSetupData = null;

          state.error = null;

          return;
        }

        state.status = "error";

        state.error = "Onverwachte MFA response.";
      })

      .addCase(verifyLoginMfa.rejected, (state, action) => {
        state.isLoading = false;

        if (state.status === "mfa_setup_required") {
          state.status = "mfa_setup_required";
        } else {
          state.status = "mfa_required";
        }

        state.error = action.payload?.message || "MFA verificatie mislukt.";
      })

      /**
       * REFRESH SESSION
       */
      .addCase(refreshAuthSession.pending, (state) => {
        state.isBootstrapping = true;
      })

      .addCase(refreshAuthSession.fulfilled, (state, action) => {
        state.isBootstrapping = false;

        const auth = normalizeAuthPayload(action.payload);

        if (auth.accessToken && auth.refreshToken) {
          tokenStorage.saveAuth(auth);

          state.status = "authenticated";

          state.accessToken = auth.accessToken;

          if (auth.user) {
            state.user = auth.user;
          }
        }
      })

      .addCase(refreshAuthSession.rejected, (state) => {
        state.isBootstrapping = false;

        tokenStorage.clearAuth();

        state.status = "idle";

        state.user = null;

        state.accessToken = null;

        state.error = null;
      })

      /**
       * CURRENT USER
       */
      .addCase(getCurrentUser.pending, (state) => {
        state.isBootstrapping = true;
      })

      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isBootstrapping = false;

        const data = action.payload?.data || {};

        state.status = "authenticated";

        state.user = data.user || data;
      })

      .addCase(getCurrentUser.rejected, (state) => {
        state.isBootstrapping = false;

        tokenStorage.clearAuth();

        state.status = "idle";

        state.user = null;

        state.accessToken = null;
      })

      /**
       * LOGOUT
       */
      .addCase(logoutUser.fulfilled, (state) => {
        tokenStorage.clearAuth();

        state.status = "idle";

        state.user = null;

        state.accessToken = null;

        state.mfaToken = null;

        state.mfaSetupData = null;

        state.error = null;
      });
  },
});

export const { clearAuthError, clearMfaFlow, logout } = authSlice.actions;

export default authSlice.reducer;
